import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import {
  PERMISSION_CATEGORIES,
  type PermissionCategoryDef,
  type PermissionCategoryState,
} from "@/services/gpsdrive-user-permissions.service";

// Lista vertical de categorias em accordion (aba PERMISSÕES, Editar
// Usuário) — substitui a antiga matriz Recurso/Visão/Editar/Excluir por
// PAPEL. Categoria OFF vence sempre: as ações internas ficam desabilitadas
// visualmente (e o backend rejeita a ação de qualquer forma, ver
// requirePermission), mesmo que o valor individual da ação continue "ON"
// por baixo — reabilitar a categoria não perde o que já estava marcado.
// "Visualizar" (pedido 2026-09-11) nunca aparece como switch próprio aqui:
// ligar a categoria já implica ver (ver withImpliedView em routes/users.tsx)
// — mostrar os dois lado a lado só confundia ("ativei a categoria e não
// deixa ver nada"). O valor ainda existe por baixo (catálogo fechado,
// backend exige a ação no payload), só não tem linha própria na UI.
export function UserPermissionsAccordion({
  values,
  onToggleCategory,
  onToggleAction,
  disabled = false,
  // Subconjunto de categorias a exibir — pedido 2026-09-03: tela de
  // Funcionário mostra só as categorias operacionais (ver
  // FUNCIONARIO_CATEGORY_KEYS), Admin/Usuário continuam com o catálogo
  // inteiro (default). Nunca redefine a categoria em si, só filtra/ordena
  // quais aparecem — `values` sempre traz o catálogo inteiro por baixo.
  categories = PERMISSION_CATEGORIES,
}: {
  values: PermissionCategoryState[];
  onToggleCategory: (category: string, enabled: boolean) => void;
  onToggleAction: (category: string, action: string, enabled: boolean) => void;
  disabled?: boolean;
  categories?: PermissionCategoryDef[];
}) {
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const byCategory = new Map(values.map((v) => [v.category, v]));
  // "administrador" é a meta-permissão de acesso total (ver
  // permission-catalog.constants.ts no backend): ligada, o backend já
  // ignora qualquer outra categoria/ação (requirePermission). Aqui é só o
  // espelho visual disso — as outras linhas aparecem travadas em ON pra não
  // sugerir que dá pra restringir algo que na prática já está liberado.
  const fullAccess = byCategory.get("administrador")?.enabled ?? false;

  return (
    <div className="flex flex-col gap-2">
      {categories.map((def) => {
        const state = byCategory.get(def.key);
        const isAdminRow = def.key === "administrador";
        const categoryEnabled = isAdminRow ? (state?.enabled ?? false) : fullAccess || (state?.enabled ?? false);
        const isOpen = openCategory === def.key;
        const expandable = def.actions.length > 0;
        const lockedOn = fullAccess && !isAdminRow;

        return (
          <div key={def.key} className="overflow-hidden rounded-lg border border-border">
            <div
              className={cn(
                "flex items-center gap-3 px-4 py-3",
                expandable && "cursor-pointer select-none",
              )}
              onClick={() => expandable && setOpenCategory(isOpen ? null : def.key)}
            >
              <span className="text-base leading-none">{def.icon}</span>
              <span className="flex-1 text-sm font-medium">{def.label}</span>
              {lockedOn && (
                <span className="text-xs text-muted-foreground">Incluso no Administrador</span>
              )}
              <div onClick={(e) => e.stopPropagation()}>
                <Switch
                  checked={categoryEnabled}
                  disabled={disabled || lockedOn}
                  onCheckedChange={(v) => {
                    onToggleCategory(def.key, v);
                    if (expandable) setOpenCategory((prev) => (v ? def.key : prev === def.key ? null : prev));
                  }}
                />
              </div>
              {expandable && (
                <ChevronDown
                  className={cn(
                    "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                    isOpen && "rotate-180",
                  )}
                />
              )}
            </div>

            {expandable && isOpen && (
              <div className="divide-y divide-border border-t border-border bg-muted/20">
                {def.hint && (
                  <p className="py-2 pl-11 pr-4 text-xs text-muted-foreground">{def.hint}</p>
                )}
                {def.actions
                  .filter((action) => action.key !== "view")
                  .map((action) => {
                    const actionState = state?.actions.find((a) => a.action === action.key);
                    const actionEnabled = lockedOn || (actionState?.enabled ?? false);
                    return (
                      <div
                        key={action.key}
                        className="flex items-center gap-3 py-2 pl-11 pr-4 text-sm"
                      >
                        <span
                          className={cn(
                            "flex-1",
                            !categoryEnabled && "text-muted-foreground",
                          )}
                        >
                          {action.label}
                        </span>
                        <Switch
                          checked={actionEnabled}
                          disabled={disabled || !categoryEnabled || lockedOn}
                          onCheckedChange={(v) => onToggleAction(def.key, action.key, v)}
                        />
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
